/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useState } from "react";
import { IPricingPlan } from "../../../../interfaces/pricingPlans";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useEntityNavigation } from "@/hooks/useEntityNavigation";
import { ESchema } from "../../../../components/table/filter/useTables";
import { debounce } from "lodash";
import { createPricingPlan, deletePricingPlan, listPricingPlans, listReasons, showPricingPlan, updatePricingPlan } from "@/services";
import { updateValue } from "@/utils/updateValue";
import { useEntityQueryParams } from "@/hooks/useEntityQueryParams";
import { useEntitySearch } from "@/hooks/useEntitySearch";
import {
  notifyDeleteResults,
  runDeleteBatch,
} from "@/utils/deleteResults";
 
const DEFAULT_DATA: IPricingPlan = {
  id: "",
  code: "",
  type: "",
  description: ""
};

export const usePricingPlans = ({
  filter: filterInitial,
  disabledShowId,
  includes = [],
  disabledGetAll,
  defaultLimit,
  id: idSelect,
}: {
  disabledGetAll?: boolean;
  disabledShowId?: boolean;
  defaultLimit?: number;
  filter?: Record<string, any>;
  includes?: string[];
  idService?: string;
  id?: string;
  typeDefault?: string;
}) => {
  const { idPricingPlan: id = idSelect || "new", idCustomer,idOutsourced } = useParams();
  const [pricingPlans, setPricingPlans] = useState<IPricingPlan[]>([]);
  const [pricingPlan, setPricingPlan] = useState<IPricingPlan>(DEFAULT_DATA);
  const { filter, handleFilter, sort, page, setPage, limit, setLimit } =
    useEntityQueryParams({ filter: filterInitial, defaultLimit });
  const [totalItems, setTotalItems] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  const { goAdd, goEdit, goBack } = useEntityNavigation({ basePath: "/towermanagement/pricing_plans" });

  const handlePricingPlan = (
    key: string,
    value: any,
    custom?: (v: any) => any
  ) => {
    setPricingPlan((curr: any) => {
      let newData = updateValue(curr, key, value);
      if (custom) newData = custom(newData);
      return { ...curr, ...newData };
    });
  };

  const getAll = useCallback(debounce(({
    page = 1,
    limit: limitP,
    filter,
    sort = { entityType: 'desc'}
  }: {
    page?: number;
    limit?: number;
    filter?: any
    sort?: Record<string, any>
  }) => {
    setLoading(true);
    listPricingPlans({ includes: [...includes], filter,  page, limit: limitP, sort })
    .then(
      ({ pricingPlans: listPricingPlans, limit, currentPage, totalItems }) => {
        setPricingPlans(listPricingPlans);
        
        setLimit(Number(limit));
        setTotalItems(Number(totalItems));
      }
    )
    .finally(() => setLoading(false));
  }, 500), []);

  const search = useEntitySearch({
    list: listPricingPlans,
    key: "pricingPlans",
    includes: ["idSite", ...includes],
    filter,
  });


  const searchReasons = async ({
    page = 1,
    limit,
    filter: filterG = {},
    includes: includesD = [],
  }: {
    page?: number;
    limit?: number;
    filter?: Record<string, any>;
    includes?: string[];
  }) => {
    const data = await listReasons({
      includes: [...includes, ...includesD],
      page,
      filter: { ...filter, ...filterG },
      limit,
    });
    return { items: data.reasons };
  };

  const get = ({ id }: { id: string | number }) => {
    setLoading(true);
    showPricingPlan({ id, includes: [] })
      .then(({ pricingPlan: pricingPlansShow }) => {
        setPricingPlan(pricingPlansShow);
      })
      .finally(() => setLoading(false));
  };

  const create = () => {
    setLoading(true);
    createPricingPlan({ pricingPlan })
      .then(({ pricingPlan: pricingPlanCreate }) => {
        toast.success(`Plano de precificação criado com sucesso!`);
        goEdit(pricingPlanCreate.id!);
      })
      .finally(() => setLoading(false));
  };



  const update = () => {
    setLoading(true);
    updatePricingPlan({ id: pricingPlan.id!, pricingPlan })
      .then(() => {
        toast.success(`Plano de precificação atualizado com sucesso!`);
        navigate(0)
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!disabledGetAll) getAll({ page, limit, filter });
  }, [page, limit, filter]);


  useEffect(() => {
    if (id === "new") {

      if (idCustomer) { 
        handlePricingPlan("idCustomer", idCustomer);
        handlePricingPlan("type", ESchema.CUSTOMERS);
       }

       if (idOutsourced) {
         handlePricingPlan("idOutsourced", idOutsourced);
         handlePricingPlan("type", ESchema.OUTSOURCEDES);
       }
       return;
    }
    if (id && !disabledShowId) {
      if (id !== "new") get({ id: id });
      return;
    }
  }, []);

  const deleteMany = async (ids: (string | number)[]) => {
    if (!ids?.length) return;
    setLoading(true);

    try {
      const results = await runDeleteBatch(ids, (entityId) =>
        deletePricingPlan({ id: entityId }),
      );

      notifyDeleteResults(results, {
        singular: "Plano de precificação",
        plural: "planos de precificação",
        gender: "m",
      });
    } catch {
      // erro sem resposta do servidor (rede/timeout): o interceptor já avisou
    } finally {
      setLoading(false);
      getAll({ page, limit, filter });
    }
  };

  return {
    deleteMany,
    pricingPlans,
    pricingPlan,
    loading,
    setPage,
    setLimit,
    page,
    limit,
    goEdit,
    goBack,
    goAdd,
    handlePricingPlan,
    create,
    id,
    update,
    totalItems,
    search,
    handleFilter,
    searchReasons,
  };
};
